import React, { useRef, useState, useEffect } from "react";

interface TwoFactorAuthFieldProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  error?: string;
  onEnter?: () => void;
}

const TwoFactorAuthField: React.FC<TwoFactorAuthFieldProps> = ({
  length = 6,
  value = "",
  onChange,
  label = "Verification Code",
  error,
  onEnter,
}) => {
  const [code, setCode] = useState<string[]>(Array(length).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Initialize the input refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Update the code when the external value changes
  useEffect(() => {
    if (value) {
      const valueArray = value.split("").slice(0, length);
      setCode(valueArray.concat(Array(length - valueArray.length).fill("")));
    }
  }, [value, length]);

  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = e.target.value;

    // Only accept numbers
    if (!/^\d*$/.test(newValue)) {
      return;
    }

    const newCode = [...code];

    // Handle pasting multiple digits
    if (newValue.length > 1) {
      const digits = newValue.split("").slice(0, length - index);

      // Fill in as many inputs as we have digits
      digits.forEach((digit, i) => {
        if (index + i < length) {
          newCode[index + i] = digit;
        }
      });

      setCode(newCode);

      // Focus the next empty input or the last input
      const nextIndex = Math.min(index + digits.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    } else {
      // Handle single digit input
      newCode[index] = newValue;
      setCode(newCode);

      // Auto-advance to next field if this one is filled
      if (newValue !== "" && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }

    // Call the onChange handler with the joined code
    const joinedCode = newCode.join("");
    onChange?.(joinedCode);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Move to next input on right arrow
    if (e.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Move to previous input on left arrow
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "Enter") {
      e.preventDefault();
      onEnter?.();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // Filter out non-numeric characters
    const pastedNumbers = pastedData.replace(/[^\d]/g, "").slice(0, length);

    if (pastedNumbers) {
      const newCode = [...code];

      // Fill the array with pasted digits and empty strings
      for (let i = 0; i < length; i++) {
        newCode[i] =
          i < pastedNumbers.length ? pastedNumbers[i] : code[i] || "";
      }

      setCode(newCode);

      // Focus the next empty field or the last field
      const nextEmptyIndex = newCode.findIndex((c) => c === "");
      const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();

      // Call onChange with the new code
      onChange?.(newCode.join(""));
    }
  };

  return (
    <div className="flex flex-col">
      {/* {label && (
        <label className="block mb-2 text-sm font-medium text-text-primary dark:text-text-primary-dark">
          {label}
        </label>
      )} */}
      <div className="flex gap-2 items-center mx-auto">
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={code[index] || ""}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            className={`w-9 h-10 text-center text-lg border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error
                ? "border-red-500 border"
                : " text-text-primary dark:text-text-primary-dark"
            }`}
            autoComplete="one-time-code"
            data-1p-ignore
            data-lpignore="true"
            autoFocus={index === 0}
          />
        ))}
      </div>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default TwoFactorAuthField;
